import DOMPurify from 'dompurify'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack, Balance, CheckCircle, Edit, OpenInNew, PhotoLibrary, PriorityHigh, PushPin, RateReview, StickyNote2 } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, Divider, Grid, LinearProgress, Skeleton, Stack, Typography } from '@mui/material'

import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { LinearProgressBar } from '../../../components/LinearProgressBar/LinearProgressBar'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import {
  useCalculatePowerScoreMutation,
  useGetActiveFeaturesQuery,
  useGetRefreshedAnalysisQuery,
  useGetScreenshotCountsQuery,
  useUpdateAnalysisVersionMutation,
} from '../../api/analysis'
import { gameNoteLegacyId, useGetAnalysisNoteQuery } from '../../api/analysis-notes'
import { useGetInternalBrandsQuery } from '../../api/brands'
import { useGetInternalCategoriesQuery } from '../../api/categories'
import { useGetInternalConventionalItemsQuery } from '../../api/conventional-genres'
import { useGetFtueVideosByGameQuery } from '../../api/ftue-videos'
import { useGetAnalyzedVersionsPerMarketQuery, useGetGameVersionsQuery } from '../../api/game-analyses'
import {
  useGetInternalGameDocumentQuery,
  useGetInternalPinnedGamesQuery,
  useSaveInternalGameMutation,
  useSaveInternalPinnedGamesMutation,
} from '../../api/games'
import { useGetInternalAnalysisCommentQuery } from '../../api/reviews'
import { AnalysisListDialog } from '../../components/AnalysisListDialog/AnalysisListDialog'
import { AnalysisNoteEditDialog } from '../../components/AnalysisNoteEditDialog/AnalysisNoteEditDialog'
import { EditGameDialog } from '../../components/EditGameDialog/EditGameDialog'
import { VersionTagsDialog } from '../../components/VersionTagsDialog/VersionTagsDialog'
import { getAnalysisScreenshotsPath } from '../../features/analysis-screenshots/helpers/screenshotPaths'
import { AnalysisCategoryCards } from '../../features/analysis/components/AnalysisCategoryCards/AnalysisCategoryCards'
import { AnalysisGameBlock } from '../../features/analysis/components/AnalysisGameBlock/AnalysisGameBlock'
import { AnalysisGameEditors } from '../../features/analysis/components/AnalysisGameEditors/AnalysisGameEditors'
import { AnalysisResultDialog } from '../../features/analysis/components/AnalysisResultDialog/AnalysisResultDialog'
import { AnalysisStatusLines } from '../../features/analysis/components/AnalysisStatusLines/AnalysisStatusLines'
import { AnalysisVersionSection } from '../../features/analysis/components/AnalysisVersionSection/AnalysisVersionSection'
import { BenchmarkGames } from '../../features/analysis/components/BenchmarkGames/BenchmarkGames'
import {
  canCalculatePowerScore,
  getTotalProgress,
  getVersionsMarketIso,
  isNewlyFinalized,
  isPreviouslyFinalized,
  toGameFieldSave,
  toOverviewCategories,
} from '../../features/analysis/helpers/analysisOverviewHelpers'
import { getAnalysisFeatureChoicesPath, getCreatedAnalysisPath, toAnalysisQueryString } from '../../features/analysis/helpers/analysisPaths'
import { useAnalysisBenchmarks } from '../../features/analysis/hooks/useAnalysisBenchmarks'
import { InternalAnalysis } from '../../features/analysis/types/InternalAnalysis'
import { ConventionalItemType } from '../../features/conventional-genres/types/InternalConventionalItem'
import { gameSaasPath } from '../../features/games/components/GameNameCell/GameNameCell'
import { gameFieldLabelKeys, getUnstoredGameFields } from '../../features/games/helpers/editGameHelpers'
import { togglePinnedGameId } from '../../features/games/helpers/gameListHelpers'
import { InternalGameDocument, InternalGameMutation } from '../../features/games/types/InternalGame'
import { gamesListPath, getAnalysisCommentEditPath, getGameReviewEditPath } from '../../features/review/helpers/reviewHelpers'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index])

export const AnalysisOverviewPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-overview'))
  const { analysisId = '' } = useParams<{ analysisId: string }>()
  // A new analysis (after a create or clone) starts with fresh page state.
  return <AnalysisOverview key={analysisId} analysisId={analysisId} />
}

const AnalysisOverview: FC<{ analysisId: string }> = ({ analysisId }) => {
  const { t } = useTranslation()
  const analysisQuery = useGetRefreshedAnalysisQuery(analysisId, { skip: !analysisId, refetchOnMountOrArgChange: true })
  const gameQuery = useGetInternalGameDocumentQuery(analysisQuery.data?.gameId ?? '', { skip: !analysisQuery.data, refetchOnMountOrArgChange: true })

  if (analysisQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_overview_load_failed')}</Typography>
  if (gameQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_overview_game_load_failed')}</Typography>
  if (!analysisQuery.data || !gameQuery.data) return <Skeleton variant="rectangular" height={480} />
  return (
    <AnalysisOverviewContent
      analysis={analysisQuery.data}
      game={gameQuery.data}
      isGameRefreshing={gameQuery.isFetching}
      refetchAnalysis={analysisQuery.refetch}
    />
  )
}

type AnalysisOverviewContentProps = {
  analysis: InternalAnalysis
  game: InternalGameDocument
  isGameRefreshing?: boolean
  refetchAnalysis: () => void
}

const AnalysisOverviewContent: FC<AnalysisOverviewContentProps> = ({ analysis, game: loadedGame, isGameRefreshing, refetchAnalysis }) => {
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  // A save PUTs the whole game, so it's built from the newest read: a save's read-back until the game is read again.
  const [game, setGame] = useState(loadedGame)
  useEffect(() => setGame(loadedGame), [loadedGame])
  const gameId = game.id ?? analysis.gameId
  const appId = game.appId || undefined
  const versionsMarketIso = getVersionsMarketIso(game, analysis.marketIso)

  const [selectedBrandId, setSelectedBrandId] = useState(typeof game.brandId === 'string' ? game.brandId : '')
  const [isEditGameOpen, setIsEditGameOpen] = useState(false)
  const [isAnalysisListOpen, setIsAnalysisListOpen] = useState(false)
  const [isVersionTagsOpen, setIsVersionTagsOpen] = useState(false)
  const [isResultOpen, setIsResultOpen] = useState(false)
  const [isGameNoteOpen, setIsGameNoteOpen] = useState(false)

  const categoriesQuery = useGetInternalCategoriesQuery({ marketIso: analysis.marketIso })
  const subgenresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Subgenre)
  const brandsQuery = useGetInternalBrandsQuery()
  const versionsQuery = useGetGameVersionsQuery({ appId: appId ?? 0, marketIso: versionsMarketIso }, { skip: !appId })
  const activeFeaturesQuery = useGetActiveFeaturesQuery(
    { appId: appId ?? 0, version: analysis.gameVersion ?? '', marketIso: analysis.marketIso, store: analysis.storeId, versionMarketIso: versionsMarketIso },
    { skip: !appId }
  )
  const perMarketQuery = useGetAnalyzedVersionsPerMarketQuery(gameId, { refetchOnMountOrArgChange: true })
  const screenshotCountsQuery = useGetScreenshotCountsQuery(gameId)
  const ftueQuery = useGetFtueVideosByGameQuery(gameId)
  const commentQuery = useGetInternalAnalysisCommentQuery(analysis.commentId ?? '', { skip: !analysis.commentId })
  const pinnedQuery = useGetInternalPinnedGamesQuery()
  const gameNoteQuery = useGetAnalysisNoteQuery({ gameId, featureLegacyId: gameNoteLegacyId })
  const gameNote = useMemo(() => gameNoteQuery.data ?? { gameId, featureLegacyId: gameNoteLegacyId, notes: '' }, [gameId, gameNoteQuery.data])
  const benchmarkState = useAnalysisBenchmarks({ gameId, gameVersion: analysis.gameVersion })
  const { benchmarks } = benchmarkState

  const [saveGame, saveGameState] = useSaveInternalGameMutation()
  const [updateVersion, updateVersionState] = useUpdateAnalysisVersionMutation()
  const [calculatePowerScore, calculateState] = useCalculatePowerScoreMutation()
  const [savePinnedGames, savePinnedState] = useSaveInternalPinnedGamesMutation()

  const categories = useMemo(() => toOverviewCategories(categoriesQuery.data ?? []), [categoriesQuery.data])
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data])
  const activeFeatures = activeFeaturesQuery.data
  const totalProgress = getTotalProgress(categories, analysis, activeFeatures)
  const canCalculate = canCalculatePowerScore({ analysis, hasGame: true, versionCount: versions.length, totalProgress })
  const finalized = isPreviouslyFinalized(analysis)
  const pinnedIds = useMemo(() => pinnedQuery.data ?? [], [pinnedQuery.data])
  const isPinned = pinnedIds.includes(gameId)
  const screenshotCount = Object.values(screenshotCountsQuery.data ?? {}).reduce((sum, count) => sum + count, 0)
  const latestAnalyzedVersion = perMarketQuery.data?.find(({ marketIso }) => marketIso === analysis.marketIso)?.gameVersion
  const isGameBusy = saveGameState.isLoading || !!isGameRefreshing
  const isBusy = isGameBusy || updateVersionState.isLoading || calculateState.isLoading

  // Legacy sends the brand picked on the page with every later game save.
  const saveGameFields = async (changes: Partial<InternalGameMutation>, successKey: string, failureKey: string) => {
    const body = toGameFieldSave(game, selectedBrandId ? { brandId: selectedBrandId, ...changes } : changes)
    const saved = await run(saveGame(body).unwrap(), { error: t(failureKey) })
    if (!saved.ok) return false
    setGame(saved.result)
    const unstored = getUnstoredGameFields(body, saved.result)
    if (unstored.length) {
      notifyError(t('internal-analysts:games_not_stored', { fields: unstored.map((field) => t(gameFieldLabelKeys[field])).join(', ') }))
      return false
    }
    notifySuccess(t(successKey))
    return true
  }

  const handleBrandChange = async (brandId: string) => {
    const previous = selectedBrandId
    setSelectedBrandId(brandId)
    const saved = await saveGameFields({ brandId }, 'internal-analysts:analysis_overview_brand_saved', 'internal-analysts:analysis_overview_brand_save_failed')
    if (!saved) setSelectedBrandId(previous)
  }

  const handleVersionChange = async (version: string) => {
    const updated = await run(updateVersion({ id: analysis.id, gameVersion: version }).unwrap(), {
      error: t('internal-analysts:analysis_overview_version_save_failed'),
    })
    if (!updated.ok) return
    if (updated.result.gameVersion !== version) {
      notifyError(t('internal-analysts:analysis_overview_version_not_stored', { version }))
      return
    }
    navigate({ search: toAnalysisQueryString({ benchmarks, version }) }, { replace: true })
    notifySuccess(t('internal-analysts:analysis_overview_version_saved', { version }))
  }

  const handleCalculate = async () => {
    const calculated = await run(calculatePowerScore(analysis.id).unwrap(), {
      error: t('internal-analysts:analysis_overview_calculate_failed'),
      errorWithReason: (reason) => t('internal-analysts:analysis_overview_calculate_failed_reason', { reason }),
    })
    if (!calculated.ok) return
    if (!isNewlyFinalized(calculated.result.before, calculated.result.stored)) {
      notifyError(t('internal-analysts:analysis_overview_calculate_not_stored'))
      return
    }
    setIsResultOpen(true)
  }

  const handleResultClose = () => {
    setIsResultOpen(false)
    refetchAnalysis()
    perMarketQuery.refetch()
  }

  // Legacy sends the whole list, so an unread list would wipe the other pins.
  const canPin = !!pinnedQuery.data && !pinnedQuery.isFetching && !savePinnedState.isLoading
  const handlePin = async () => {
    const next = togglePinnedGameId(pinnedIds, gameId)
    const saved = await run(savePinnedGames(next).unwrap(), { error: t('internal-analysts:games_pin_failed') })
    if (!saved.ok) return
    if (!sameIds(saved.result, next)) {
      notifyError(t('internal-analysts:games_pin_not_stored'))
      return
    }
    notifySuccess(t(isPinned ? 'internal-analysts:games_unpinned' : 'internal-analysts:games_pinned', { name: game.name }))
  }

  const openCreatedAnalysis = (createdId: string, comparedMarketIso?: string) => {
    setIsAnalysisListOpen(false)
    setIsResultOpen(false)
    navigate(getCreatedAnalysisPath(createdId, gameId, comparedMarketIso))
  }

  const getCategoryPath = (categoryId: string) => getAnalysisFeatureChoicesPath(analysis.id, categoryId, { benchmarks, version: analysis.gameVersion })

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(gamesListPath)}>
          {t('internal-analysts:analysis_overview_back_to_games')}
        </Button>
        <Button size="small" startIcon={<Balance />} onClick={() => setIsAnalysisListOpen(true)}>
          {t('internal-analysts:games_view_analyses')}
        </Button>
        <Button size="small" variant="outlined" startIcon={<Edit />} disabled={isGameBusy} onClick={() => setIsEditGameOpen(true)}>
          {t('internal-analysts:games_edit')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RateReview />}
          disabled={!appId}
          href={getGameReviewEditPath(appId ?? '', game.reviewId, analysis.id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('internal-analysts:analysis_overview_edit_review')}
        </Button>
        <Button size="small" variant="outlined" endIcon={<OpenInNew />} href={gameSaasPath(gameId)} target="_blank" rel="noopener noreferrer">
          {t('internal-analysts:analysis_overview_open_saas')}
        </Button>
        <Button
          size="small"
          variant={isPinned ? 'contained' : 'outlined'}
          color={isPinned ? 'success' : 'primary'}
          startIcon={isPinned ? <CheckCircle /> : <PushPin />}
          disabled={!canPin}
          onClick={handlePin}
        >
          {t(isPinned ? 'internal-analysts:games_unpin' : 'internal-analysts:games_pin')}
        </Button>
      </Stack>
      {pinnedQuery.isError && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:games_pinned_load_failed')}
        </Typography>
      )}

      <Grid container wrap="wrap" spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <AnalysisGameEditors
              game={game}
              subgenres={subgenresQuery.data ?? []}
              subgenresFailed={subgenresQuery.isError}
              brands={brandsQuery.data ?? []}
              brandsFailed={brandsQuery.isError}
              selectedBrandId={selectedBrandId}
              disabled={isBusy}
              onSubgenreChange={(conventionalSubgenre) =>
                saveGameFields(
                  { conventionalSubgenre },
                  'internal-analysts:analysis_overview_subgenre_saved',
                  'internal-analysts:analysis_overview_subgenre_save_failed'
                )
              }
              onOverrideChange={(overrideConventionals) =>
                saveGameFields(
                  { overrideConventionals },
                  'internal-analysts:analysis_overview_subgenre_saved',
                  'internal-analysts:analysis_overview_subgenre_save_failed'
                )
              }
              onBrandChange={handleBrandChange}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Button size="small" variant="outlined" startIcon={<PhotoLibrary />} onClick={() => navigate(getAnalysisScreenshotsPath(analysis.id))}>
                {t('internal-analysts:analysis_overview_screenshots')}
              </Button>
              <Typography variant="body2">
                {t('internal-analysts:analysis_overview_analysis_screenshots', { count: analysis.screenshots?.length ?? 0 })}
              </Typography>
            </Stack>
            <BenchmarkGames benchmarks={benchmarks} disabled={benchmarkState.disabled} onPick={benchmarkState.pick} onRemove={benchmarkState.remove} />
            {benchmarkState.settingFailed && <Typography color="error.main">{t('internal-analysts:analysis_overview_benchmarks_load_failed')}</Typography>}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <AnalysisGameBlock title={t('internal-analysts:analysis_overview_currently_analyzing')} game={game} marketIso={analysis.marketIso} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Stack spacing={3}>
        <AnalysisStatusLines
          marketIso={analysis.marketIso}
          latestAnalyzedVersion={{ value: latestAnalyzedVersion || '-', isLoading: perMarketQuery.isLoading, failed: perMarketQuery.isError }}
          hasFtue={{ value: !!ftueQuery.data?.length, isLoading: ftueQuery.isLoading, failed: ftueQuery.isError }}
          reviewPublished={!!game.reviewPublished}
          screenshotCount={{ value: screenshotCount, isLoading: screenshotCountsQuery.isLoading, failed: screenshotCountsQuery.isError }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<StickyNote2 />} disabled={!gameNoteQuery.isSuccess} onClick={() => setIsGameNoteOpen(true)}>
            {t('internal-analysts:analysis_overview_game_notes')}
          </Button>
          {gameNoteQuery.data?.notes && (
            <GRTooltip content={<Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(gameNoteQuery.data.notes) }} />}>
              <PriorityHigh color="success" aria-label={t('internal-analysts:analysis_overview_has_game_note')} />
            </GRTooltip>
          )}
          {gameNoteQuery.isError && <Typography color="error.main">{t('internal-analysts:analysis_overview_game_notes_failed')}</Typography>}
        </Stack>
        {categoriesQuery.isError && <Typography color="error.main">{t('internal-analysts:analysis_overview_categories_failed')}</Typography>}
        {activeFeaturesQuery.isError && <Typography color="error.main">{t('internal-analysts:analysis_overview_active_features_failed')}</Typography>}
        {categoriesQuery.isLoading ? (
          <Skeleton variant="rectangular" height={240} />
        ) : (
          <AnalysisCategoryCards categories={categories} analysis={analysis} activeFeatures={activeFeatures} getCategoryPath={getCategoryPath} />
        )}
        <AnalysisVersionSection
          analysis={analysis}
          versions={versions}
          isLoadingVersions={versionsQuery.isLoading}
          versionsFailed={versionsQuery.isError}
          canCalculate={canCalculate && !categoriesQuery.isLoading && !activeFeaturesQuery.isLoading}
          disabled={isBusy}
          comment={commentQuery.data}
          commentEditUrl={getAnalysisCommentEditPath({ gameId, gameVersion: analysis.gameVersion, commentId: analysis.commentId, analysisId: analysis.id })}
          onVersionChange={handleVersionChange}
          onCalculate={handleCalculate}
          onEditVersionTags={() => setIsVersionTagsOpen(true)}
        />
      </Stack>

      <Box
        sx={{ position: 'sticky', bottom: 0, mt: 3, py: 1.5, px: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', zIndex: 1 }}
        data-testid="total-progress"
      >
        <LinearProgressBar value={totalProgress} labelAfter={`${totalProgress}%`} color={totalProgress === 100 ? 'success' : 'primary'} />
      </Box>

      <Dialog open={calculateState.isLoading} fullWidth maxWidth="sm">
        <DialogContent>
          <Stack spacing={2}>
            <Typography textAlign="center">
              {t(finalized ? 'internal-analysts:analysis_overview_recalculating' : 'internal-analysts:analysis_overview_calculating')}
            </Typography>
            <LinearProgress />
          </Stack>
        </DialogContent>
      </Dialog>
      <AnalysisResultDialog
        open={isResultOpen}
        analysisId={analysis.id}
        gameId={gameId}
        game={game}
        onClose={handleResultClose}
        onCloned={openCreatedAnalysis}
        onCloneFailed={() => notifyError(t('internal-analysts:games_analyses_create_failed'))}
      />
      <AnalysisListDialog open={isAnalysisListOpen} gameId={gameId} onClose={() => setIsAnalysisListOpen(false)} onAnalysisCreated={openCreatedAnalysis} />
      <AnalysisNoteEditDialog open={isGameNoteOpen} note={gameNote} onClose={() => setIsGameNoteOpen(false)} />
      <EditGameDialog open={isEditGameOpen} gameId={gameId} onClose={() => setIsEditGameOpen(false)} />
      <VersionTagsDialog
        open={isVersionTagsOpen}
        appId={appId}
        gameName={game.name ?? ''}
        marketIso={analysis.marketIso}
        onClose={() => setIsVersionTagsOpen(false)}
      />
    </Box>
  )
}
